import React, { useState } from "react";
import { DateTime } from "luxon";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMCaseTable } from "../../components/CRM/CRMCases/CRMCaseTable/CRMCaseTable";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { CRMCaseTableFilter } from "../../components/CRM/CRMCases/CRMCaseTableFilter/CRMCaseTableFilter";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMDayTableFilter } from "../../components/CRM/CRMCases/CRMDayTableFilter/CRMDayTableFilter";
import { CRMDayRoadblockFilter } from "../../components/CRM/CRMCases/CRMDayRoadblockTable/CRMDayRoadblockFilter";
import { getRoadblockChecklistItemOptions, roadblockTypeOptions } from "../../../../domain/roadblocks/roadblockChecklistDisplay";
import { CRMDaySortFilter } from "../../components/CRM/CRMCases/CRMDaySortFilter/CRMDaySortFilter";
import { CRMDayWeekStrip } from "../../components/CRM/CRMCases/CRMDayWeekStrip/CRMDayWeekStrip";
import { CRMDayOverdueBanner } from "../../components/CRM/CRMCases/CRMDayOverdueBanner/CRMDayOverdueBanner";
import { CRMDayCombinedTable } from "../../components/CRM/CRMCases/CRMDayCombinedTable/CRMDayCombinedTable";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { CRMCaseViewFilter } from "../../components/CRM/CRMCases/CRMCaseViewFilter/CRMCaseViewFilter";
import { Background } from "../../components/BuildingBlocks/Background";
import { CRMSailHomesReport } from "../../components/CRM/CRMCases/CRMSailHomesReport/CRMSailHomesReport";
import { CRMSailHomesReportFilter } from "../../components/CRM/CRMCases/CRMSailHomesReport/CRMSailHomesReportFilter";
import { createChangeRouteAction } from "../../state/router/createChangeRouteAction";
import { Routes } from "../../state/router/routerRoutes";
import { makeJobLinkOutOfRouteAction } from "../../components/CRMJobs/CRMJobsOverlay/CRMJobsCardLinkGenerators";
import { hasAssigneeLeftTheDayList, hasDateLeftTheDayList } from "../../functions/dayViewDateWindow";

const CRMCaseTableContainer = (props: TContainerStateProps): JSX.Element => {

    const view = props.state.forms.cases_page.view;
    const isCaseView = view === "case";
    const isDayView = view === "day";
    const isSailHomesReport = view === "sail_homes_report";

    const sailHomesReportForm = props.state.forms.cases_page.sail_homes_report_form;

    const dispatchSailHomesReportFilters = (partial: Partial<typeof sailHomesReportForm.edited.filters>): void =>
        props.dispatch({
            type: "CRM_NEW_CASES_SAIL_HOMES_REPORT_FILTER_CHANGE",
            payload: {
                ...sailHomesReportForm,
                edited: {
                    ...sailHomesReportForm.edited,
                    filters: { ...sailHomesReportForm.edited.filters, ...partial },
                },
            },
        });

    const goToCasePage = (caseId: string): void => {
        window.open(
            makeJobLinkOutOfRouteAction(
                createChangeRouteAction(
                    "VIEW_CRM_LEGAL_CASE",
                    { caseId },
                    { visible_tab: "details" },
                    new Routes().blockParams.VIEW_CRM_LEGAL_CASE,
                ),
            ),
            "_blank",
        );
    };

    const dayViewForm = props.state.forms.cases_page.day_view_form;
    const dayViewRoadblocksForm = props.state.forms.cases_page.day_view_roadblocks_form;
    const weekCountsForm = props.state.forms.cases_page.day_view_week_counts_form;
    const bulkRescheduleForm = props.state.forms.cases_page.day_view_bulk_reschedule_form;
    const bulkAwaitForm = props.state.forms.cases_page.day_view_bulk_await_form;
    const roadblocksAssignedTo = dayViewRoadblocksForm.edited.filters.assigned_to;
    const sessionUserId = props.state.global.user.id;

    const roadblocksViewingName: string | null =
        roadblocksAssignedTo === null
            ? "The team"
            : roadblocksAssignedTo === sessionUserId
                ? null
                : (() => {
                    const assignee = dayViewForm.children.assignable_users
                        .find((user) => user.id === roadblocksAssignedTo);
                    return assignee ? assignee.first_name : "Someone else";
                })();

    const dispatchDayDateFilter = (date: { type: "iso", date: string } | { type: "overdue" }) =>
        props.dispatch({
            type: "CRM_NEW_CASES_DAY_VIEW_FILTER_CHANGE",
            payload: {
                ...dayViewForm,
                edited: {
                    ...dayViewForm.edited,
                    filters: {
                        ...dayViewForm.edited.filters,
                        date,
                    },
                },
            },
        });

    const dayDateFilter = dayViewForm.edited.filters.date;
    const londonTodayIso = DateTime.utc().setZone("Europe/London").toISODate();
    const dayListLabel =
        dayDateFilter.type === "overdue" ? "Overdue"
        : dayDateFilter.date === londonTodayIso ? "Today"
        : DateTime.fromISO(dayDateFilter.date).toFormat("cccc d LLLL");

    const dayListCount = dayViewForm.children.available_count;

    const getAheadFromIso = dayDateFilter.type === "iso" ? dayDateFilter.date : londonTodayIso;
    const getAheadDay = (() => {
        const nextBusyDay = weekCountsForm.children.days.find((day) =>
            day.date > getAheadFromIso
            && day.tasks_count + day.roadblocks_count > 0
        );
        if (nextBusyDay === undefined) {
            return null;
        }
        return {
            isoDate: nextBusyDay.date,
            label: DateTime.fromISO(nextBusyDay.date).toFormat("cccc"),
            count: nextBusyDay.tasks_count + nextBusyDay.roadblocks_count,
        };
    })();

    return (
        <SpacingColumn
            spacing={CRMSpacing.LARGE}
        >
            {/* FILTERS */}
            <Background padding={`0px ${CRMSpacing.X_LARGE}`}>
                <SpacingRow
                    justifyContent="space-between"
                    spacing={CRMSpacing.LARGE}
                >
                    {/* LEFT - CASE VIEW*/}
                    {isCaseView &&
                        <CRMCaseTableFilter
                            form={props.state.forms.cases_page.cases_view_form}
                            onChangeForm={(payload) => props.dispatch({
                                type: "CRM_NEW_CASES_CASE_VIEW_FILTER_CHANGE",
                                payload,
                            })}
                        />
                    }

                    {/* LEFT - DAY VIEW */}
                    {isDayView &&
                        <CRMDayTableFilter
                            form={dayViewForm}
                            onChangeForm={(payload) => props.dispatch({
                                type: "CRM_NEW_CASES_DAY_VIEW_FILTER_CHANGE",
                                payload,
                            })}
                        />
                    }

                    {/* LEFT - SAIL HOMES REPORT */}
                    {isSailHomesReport &&
                        <CRMSailHomesReportFilter
                            form={sailHomesReportForm}
                            onToggleUnderOffer={(under_offer_only) => dispatchSailHomesReportFilters({ under_offer_only })}
                            onToggleNeedsAction={(needs_action_only) => dispatchSailHomesReportFilters({ needs_action_only })}
                            onChangeAssignedTo={(assigned_to) => dispatchSailHomesReportFilters({ assigned_to })}
                        />
                    }

                    {/* RIGHT - VIEW SWITCH */}
                    <CRMCaseViewFilter
                        view={view}
                        onChangeView={(payload) => props.dispatch({
                            type: "CRM_NEW_CASES_VIEW_CHANGE",
                            payload,
                        })}
                    />
                </SpacingRow>
            </Background>

            {/* TABLE - CASE VIEW */}
            {isCaseView &&
                <CRMCaseTable
                    form={props.state.forms.cases_page.cases_view_form}
                    assignableUsers={props.state.forms.cases_page.cases_view_form.children.adhoc_assignable_users}
                    openCaseId={props.state.forms.cases_page.cases_view_open_case_id}
                    onSetOpenCaseId={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_CASE_VIEW_OPEN_CASE_ID_CHANGE",
                        payload,
                    })}
                    onChangeViewForm={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_CASE_VIEW_SORTING_CHANGE",
                        payload,
                    })}
                    onLoadMore={() => props.dispatch({
                        type: "CRM_NEW_CASES_CASE_VIEW_LOAD_MORE",
                    })}
                    onChangeCaseFormComment={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_CASE_VIEW_COMMENT_CHANGE",
                        payload,
                    })}
                    onSubmitCaseForm={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_CASE_VIEW_CASE_SUBMIT",
                        payload,
                    })}
                    onArchiveTask={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_ARCHIVE_TASK",
                        payload,
                    })}
                    onChangeTask={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_UPDATE_TASK",
                        payload,
                    })}
                    onSubmitNewTask={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_SUBMIT_NEW_TASK",
                        payload,
                    })}
                />
            }

            {/* SAIL HOMES DAILY REPORT */}
            {isSailHomesReport &&
                <CRMSailHomesReport
                    form={sailHomesReportForm}
                    onLoadMore={() => props.dispatch({ type: "CRM_NEW_CASES_SAIL_HOMES_REPORT_LOAD_MORE" })}
                    onExpand={(caseId) => props.dispatch({ type: "CRM_NEW_CASES_SAIL_HOMES_REPORT_EXPAND", payload: caseId })}
                    onGoToCase={goToCasePage}
                    onChangeTask={(caseId, taskForm) => props.dispatch({ type: "CRM_NEW_CASES_SAIL_HOMES_REPORT_UPDATE_TASK", payload: [caseId, taskForm] })}
                    onArchiveTask={(caseId, taskForm) => props.dispatch({ type: "CRM_NEW_CASES_SAIL_HOMES_REPORT_ARCHIVE_TASK", payload: [caseId, taskForm] })}
                    onSubmitNewTask={(caseId, taskForm) => props.dispatch({ type: "CRM_NEW_CASES_SAIL_HOMES_REPORT_SUBMIT_NEW_TASK", payload: [caseId, taskForm] })}
                />
            }

            {/* WEEK STRIP - DAY VIEW */}
            {isDayView &&
                <CRMDayWeekStrip
                    form={weekCountsForm}
                    dateFilter={dayDateFilter}
                    viewingName={roadblocksViewingName}
                    onPickDate={(date) => dispatchDayDateFilter({ type: "iso", date })}
                    onPickOverdue={() => dispatchDayDateFilter({ type: "overdue" })}
                />
            }

            {/* OVERDUE BANNER - DAY VIEW */}
            {isDayView &&
                <CRMDayOverdueBanner
                    reschedulableTasksCount={weekCountsForm.children.overdue.reschedulable_tasks_count}
                    keyDatesCount={weekCountsForm.children.overdue.key_dates_count}
                    roadblocksCount={weekCountsForm.children.overdue.roadblocks_count}
                    assignedTo={dayViewForm.edited.filters.assigned_to}
                    sailHomesListingOnly={dayViewForm.edited.filters.sail_homes_listing_only}
                    bulkRescheduleForm={bulkRescheduleForm}
                    bulkAwaitForm={bulkAwaitForm}
                    onBulkAwait={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_DAY_VIEW_BULK_AWAIT",
                        payload,
                    })}
                    onUndoBulkAwait={() => props.dispatch({
                        type: "CRM_NEW_CASES_DAY_VIEW_BULK_AWAIT_UNDO",
                    })}
                    onReviewOverdue={() => dispatchDayDateFilter({ type: "overdue" })}
                    onReschedule={(payload) => props.dispatch({
                        type: "CRM_NEW_CASES_DAY_VIEW_BULK_RESCHEDULE",
                        payload,
                    })}
                    onUndo={() => props.dispatch({
                        type: "CRM_NEW_CASES_DAY_VIEW_BULK_RESCHEDULE_UNDO",
                    })}
                />
            }

            {/* COMBINED WORK LIST - DAY VIEW */}
            {isDayView &&
                <>
                    <Background padding={`0px ${CRMSpacing.X_LARGE}`}>
                        <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center">
                            <CRMParagraph>
                                <WeightBold>{dayListLabel}</WeightBold>
                                {` (${dayListCount})`}
                            </CRMParagraph>
                            <CRMDayRoadblockFilter
                                types={roadblockTypeOptions}
                                getItemOptions={getRoadblockChecklistItemOptions}
                                value={{
                                    source: dayViewRoadblocksForm.edited.filters.source,
                                    roadblock_type: dayViewRoadblocksForm.edited.filters.roadblock_type,
                                    checklist_item_type: dayViewRoadblocksForm.edited.filters.roadblock_checklist_item_type,
                                }}
                                onChange={(value) => {
                                    props.dispatch({
                                        type: "CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_FILTER_CHANGE",
                                        payload: {
                                            source: value.source,
                                            roadblock_type: value.roadblock_type,
                                            checklist_item_type: value.checklist_item_type,
                                        },
                                    });
                                }}
                            />
                            <CRMDaySortFilter
                                value={dayViewForm.edited.filters.sort}
                                onChange={(sort) => {
                                    props.dispatch({
                                        type: "CRM_NEW_CASES_DAY_VIEW_SORT_CHANGE",
                                        payload: sort,
                                    });
                                }}
                            />
                        </SpacingRow>
                    </Background>
                    <CRMDayCombinedTable
                        dayForm={dayViewForm}
                        roadblocksForm={dayViewRoadblocksForm}
                        sort={dayViewForm.edited.filters.sort}
                        assignableUsers={dayViewForm.children.assignable_users}
                        isDateShown={dayDateFilter.type === "overdue"}
                        isDateLeavingTheList={(date) => hasDateLeftTheDayList(dayDateFilter, date)}
                        isAssigneeLeavingTheList={(assignedToUserId) =>
                            hasAssigneeLeftTheDayList(dayViewForm.edited.filters.assigned_to, assignedToUserId)
                        }
                        onLoadMore={() => {
                            props.dispatch({
                                type: "CRM_NEW_CASES_DAY_VIEW_LOAD_MORE",
                            });
                            props.dispatch({
                                type: "CRM_NEW_CASES_DAY_VIEW_ROADBLOCKS_LOAD_MORE",
                            });
                        }}
                        onArchiveTask={(payload) => props.dispatch({
                            type: "CRM_NEW_CASES_ARCHIVE_DAY_TASK",
                            payload,
                        })}
                        onChangeTask={(payload) => props.dispatch({
                            type: "CRM_NEW_CASES_UPDATE_DAY_TASK",
                            payload,
                        })}
                        onChangeRoadblockAssignee={(payload) => props.dispatch({
                            type: "CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_ASSIGN",
                            payload,
                        })}
                        onAwaitRoadblock={(payload) => props.dispatch({
                            type: "CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_AWAIT",
                            payload,
                        })}
                        onCancelAwaitRoadblock={(payload) => props.dispatch({
                            type: "CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_CANCEL_AWAIT",
                            payload,
                        })}
                        getAheadDay={getAheadDay}
                        onGoToDate={(date) => dispatchDayDateFilter({ type: "iso", date })}
                    />
                </>
            }
        </SpacingColumn>
    );
}

export default CRMCaseTableContainer;
